import { createApi } from '@reduxjs/toolkit/query/react';
import { B2C_SUB_SYSTEM_ID } from 'core';
import { ComponentDetail } from 'store/basket/basketAPI';

import { axiosBaseQuery } from '../axiosBaseQuery';

export interface AwardingBody extends Option {
  id: number;
}

export interface CurrentSession extends Option {
  awardingBodyId: number;
  isCurrent: boolean;
  notificationInfo: { content: string; type: string } | null;
  productSubSystemId: string;
  sessionId: number;
  sessionStart: Date;
  sessionEnd: Date;
  checkpointOnly: boolean;
  stageId: number;
}

export interface QualificationLevel extends Option {
  id: number;
  isCheckpoint: boolean;
}

export interface Location extends Option {
  centre: unknown;
  id: number;
  name: string;
}

export interface Subject extends Option {
  code: string;
  syllabusId: number;
  syllabusName: string;
}

export interface Exam {
  boardFee: number;
  code: string;
  componentDetails: ComponentDetail[];
  currency: string;
  districtExamId: number;
  eocfId: number;
  examOptonTypeCode: number;
  examSyllabusId: number;
  fee: number;
  id: number;
  isCashin: boolean;
  name: string;
  qualificationLevel: string;
  qualificationLevelId: number;
  retakeAvailable: boolean;
  retakeBoardFee: number;
  retakeFee: number;
  retakeSelected: boolean;
  sessionId: number;
  sessionStageId: number;
  syllabusCode: string;
  syllabusName: string;
  isCarryForward: boolean;
}

export interface Session {
  awardingBodyId: number;
  awardingBodyName: string;
  carryForwardAvailable: boolean;
  checkpointOnly: boolean;
  isArchived: boolean;
  isCurrent: boolean;
  isFuture: boolean;
  name: string;
  productSubSystemId: number;
  retakeAvailable: boolean;
  sessionEnd: string;
  sessionId: number;
  sessionStageEnd: string;
  sessionStart: string;
  stageId: number | null;
}

interface TimelineData {
  sessionName: string;
  title: string;
  items: {
    date: string;
    event: string;
    isActive: boolean;
    isCurrent: boolean;
  }[];
  itemsQlg: {
    qualificationLevelGroupId: number;
    qualificationLevelGroupName: string;
    itemList: {
      date: string;
      event: string;
      isActive: boolean;
      isCurrent: boolean;
    }[];
  }[];
}

export const examsSearchApi = createApi({
  reducerPath: 'examsSearchApi',
  baseQuery: axiosBaseQuery({
    baseUrl: '/web-api/api',
  }),
  endpoints: (builder) => ({
    getAwardingBody: builder.query<AwardingBody[], void>({
      query: () => ({ url: `/exams/awardingBody`, method: 'get' }),
      transformResponse: (response: AwardingBodyResponse[]) =>
        response.map((awardingBody) => ({
          value: awardingBody.Id.toString(),
          name: awardingBody.Name,
          id: awardingBody.Id,
        })),
    }),
    getCurrentSessions: builder.query<CurrentSession[], string | void>({
      query: (awardingBody) => ({
        url: `/exams/currentSessions${awardingBody ? '/' + awardingBody : ''}`,
        method: 'get',
      }),
      transformResponse: mapSessionsResponse,
    }),
    getCurrentSessionsWithNotification: builder.query<CurrentSession[], string>({
      query: (awardingBody) => ({
        url: `/exams/currentSessionsWithNotification/${awardingBody}`,
        method: 'get',
      }),
      transformResponse: mapSessionsResponse,
    }),
    getQualificationLevels: builder.query<QualificationLevel[], QualificationLevelParams>({
      query: ({ awardingBody, countryId, productSubSystemId, sessionId }) => ({
        url: `/exams/qualificationLevels/${countryId}/${awardingBody}/${sessionId}/${productSubSystemId}`,
        method: 'get',
      }),
      transformResponse: (response: QualificationLevelResponse[]) =>
        response.map((qualificationLevel) => ({
          id: qualificationLevel.Id,
          isCheckpoint: qualificationLevel.IsCheckpoint,
          name: qualificationLevel.Name,
          value: qualificationLevel.Id.toString(),
        })),
    }),
    getLocations: builder.query<Location[], LocationsParams>({
      query: ({ awardingBody, countryId, productSubSystemId, qualificationLevel, sessionId }) => ({
        url: `/exams/districts/${countryId}/${awardingBody}/${qualificationLevel}/${productSubSystemId}/0/0/${sessionId}`,
        method: 'get',
      }),
      transformResponse: (response: LocationResponse[]) =>
        response.map((location) => ({
          centre: location.Centre,
          id: location.Id,
          name: location.Name,
          value: location.Id.toString(),
        })),
    }),
    getSubjects: builder.query<Subject[], SubjectParams>({
      query: ({ awardingBody, location, qualificationLevel, sessionId, productSubSystemId }) => ({
        url: `/exams/subjectsWithExistsExam/${awardingBody}/${sessionId}/${qualificationLevel}/${location}/${productSubSystemId}`,
        method: 'get',
      }),
      transformResponse: (response: SubjectResponse[]) =>
        response.map((subject) => ({
          code: subject.Code,
          syllabusId: subject.SyllabusId,
          syllabusName: subject.SyllabusName,
          value: subject.SyllabusId.toString(),
          name: `${subject.Code} - ${subject.SyllabusName}`,
        })),
    }),
    getSubjectsForHybrid: builder.query<Subject[], HybridSubjectParams>({
      query: ({ awardingBody, location, qualificationLevel, sessionId, preparationCentreId }) => ({
        url: `/exams/subjectsWithExistsExam/${awardingBody}/${sessionId}/${qualificationLevel}/${location}/hybrid/${preparationCentreId}`,
        method: 'get',
      }),
      transformResponse: (response: SubjectResponse[]) =>
        response.map((subject) => ({
          code: subject.Code,
          syllabusId: subject.SyllabusId,
          syllabusName: subject.SyllabusName,
          value: subject.SyllabusId.toString(),
          name: `${subject.Code} - ${subject.SyllabusName}`,
        })),
    }),
    searchExams: builder.query<Exam[], ExamParams>({
      query: (data) => ({ url: `/exams/examoptions/search`, method: 'post', data }),
      transformResponse: (response: ExamResponse[]) =>
        response.map((exam) => ({
          boardFee: exam.BoardFee,
          code: exam.Code,
          componentDetails: exam.ComponentDetails?.map((x) => ({
            name: x.Name,
            sessionTime: x.SessionTime,
            examDate: x.ExamDate,
          })),
          currency: exam.Currency,
          districtExamId: exam.DistrictExamId,
          eocfId: exam.EocfId,
          examOptonTypeCode: exam.ExamOptonTypeCode,
          examSyllabusId: exam.ExamSyllabusId,
          fee: exam.Fee,
          id: exam.Id,
          isCashin: exam.IsCashin,
          name: exam.Name,
          qualificationLevel: exam.QualificationLevel,
          qualificationLevelId: exam.QualificationLevelId,
          retakeAvailable: exam.RetakeAvailable,
          retakeBoardFee: exam.RetakeBoardFee,
          retakeSelected: exam.RetakeSelected,
          retakeFee: exam.RetakeFee,
          sessionId: exam.SessionId,
          sessionStageId: exam.SessionStageId,
          syllabusCode: exam.SyllabusCode,
          syllabusName: exam.SyllabusName,
          isCarryForward: exam.IsCarryForward,
        })),
    }),
    getAllSessions: builder.query<Session[], void>({
      query: () => ({ url: `/exams/allSessions`, method: 'get' }),
      transformResponse: (response: SessionResponse[]) =>
        response.map((session) => ({
          awardingBodyId: session.AwardingBodyId,
          awardingBodyName: session.AwardingBodyName,
          carryForwardAvailable: session.CarryForwardAvailable,
          checkpointOnly: session.CheckpointOnly,
          isArchived: session.IsArchived,
          isCurrent: session.IsCurrent,
          isFuture: session.IsFuture,
          name: session.Name,
          productSubSystemId: session.ProductSubSystemId,
          retakeAvailable: session.RetakeAvailable,
          sessionEnd: session.SessionEnd,
          sessionId: session.SessionId,
          sessionStageEnd: session.SessionStageEnd,
          sessionStart: session.SessionStart,
          stageId: session.StageId,
        })),
    }),
    getTimeline: builder.query<TimelineData, number>({
      query: (sessionId: number) => ({ url: `/timelines/session/${sessionId}`, method: 'get' }),
      transformResponse: (response: TimelineDataResponse) => ({
        sessionName: response.SessionName,
        title: response.Title,
        items: response.Items.map((x) => ({
          date: x.Date,
          event: x.Event,
          isActive: x.IsActive,
          isCurrent: x.IsCurrent,
        })),
        itemsQlg: response.ItemsQlg.map((x) => ({
          qualificationLevelGroupId: x.QualificationLevelGroupId,
          qualificationLevelGroupName: x.QualificationLevelGroupName,
          itemList: x.ItemList.map((y) => ({
            date: y.Date,
            event: y.Event,
            isActive: y.IsActive,
            isCurrent: y.IsCurrent,
          })),
        })),
      }),
    }),
  }),
});

export const {
  useGetAwardingBodyQuery,
  useLazyGetCurrentSessionsQuery,
  useGetCurrentSessionsQuery,
  useLazyGetQualificationLevelsQuery,
  useLazyGetLocationsQuery,
  useLazyGetSubjectsQuery,
  useLazyGetSubjectsForHybridQuery,
  useLazySearchExamsQuery,
  useGetAllSessionsQuery,
  useLazyGetCurrentSessionsWithNotificationQuery,
  useLazyGetTimelineQuery,
} = examsSearchApi;

interface Option {
  name: string;
  value: string;
}
interface AwardingBodyResponse {
  CurrentRetakeWindowDates: unknown;
  Id: number;
  Name: string;
  ShortCode: string;
}

interface CurrentSessionResponse {
  NotificationInfo: { Content: string; Type: string } | null;
  SessionId: number;
  SessionStart: Date;
  SessionEnd: Date;
  IsCurrent: boolean;
  Name: string;
  AwardingBodyId: number;
  ProductSubSystemId: string;
  CheckpointOnly: boolean;
  StageId: number;
}

interface QualificationLevelParams {
  awardingBody: string;
  countryId: string;
  productSubSystemId: string;
  sessionId: string;
}

interface QualificationLevelResponse {
  Id: number;
  Name: string;
  IsCheckpoint: boolean;
}

interface LocationsParams extends QualificationLevelParams {
  qualificationLevel: string;
}

interface LocationResponse {
  Centre: unknown;
  Id: number;
  Name: string;
}

interface SubjectParams {
  awardingBody: string;
  location: string;
  qualificationLevel: string;
  sessionId: string;
  productSubSystemId: string;
}

interface HybridSubjectParams {
  awardingBody: string;
  location: string;
  qualificationLevel: string;
  sessionId: string;
  preparationCentreId: string;
}

interface SubjectResponse {
  Code: string;
  SyllabusId: number;
  SyllabusName: string;
}

interface ExamParams {
  awardingBodyId: number;
  districtId: number;
  examSyllabusIds: number[];
  qualificationLevelId: number;
  productSubSystemId: number;
  sessionId: number;
}

interface ComponentResponse {
  Name: string;
  SessionTime: string;
  ExamDate: Date;
}

interface ExamResponse {
  BoardFee: number;
  Code: string;
  ComponentDetails: ComponentResponse[];
  Currency: string;
  DistrictExamId: number;
  EocfId: number;
  ExamOptonTypeCode: number;
  ExamSyllabusId: number;
  Fee: number;
  Id: number;
  IsCashin: boolean;
  Name: string;
  QualificationLevel: string;
  QualificationLevelId: number;
  RetakeAvailable: boolean;
  RetakeBoardFee: number;
  RetakeFee: number;
  RetakeSelected: boolean;
  SessionId: number;
  SessionStageId: number;
  SyllabusCode: string;
  SyllabusName: string;
  IsCarryForward: boolean;
}

interface SessionResponse {
  AwardingBodyId: number;
  AwardingBodyName: string;
  CarryForwardAvailable: boolean;
  CheckpointOnly: boolean;
  IsArchived: boolean;
  IsCurrent: boolean;
  IsFuture: boolean;
  Name: string;
  ProductSubSystemId: number;
  RetakeAvailable: boolean;
  SessionEnd: string;
  SessionId: number;
  SessionStageEnd: string;
  SessionStart: string;
  StageId: number | null;
}

interface TimelineDataResponse {
  SessionName: string;
  Title: string;
  Items: {
    Date: string;
    Event: string;
    IsActive: boolean;
    IsCurrent: boolean;
  }[];
  ItemsQlg: {
    QualificationLevelGroupId: number;
    QualificationLevelGroupName: string;
    ItemList: {
      Date: string;
      Event: string;
      IsActive: boolean;
      IsCurrent: boolean;
    }[];
  }[];
}

function mapSessionsResponse(response: CurrentSessionResponse[]): CurrentSession[] {
  return response.map((session) => ({
    awardingBodyId: session.AwardingBodyId,
    isCurrent: session.IsCurrent,
    name: session.Name,
    notificationInfo: session.NotificationInfo
      ? { content: session.NotificationInfo.Content, type: session.NotificationInfo.Type }
      : null,
    productSubSystemId: B2C_SUB_SYSTEM_ID.toString(),
    sessionId: session.SessionId,
    sessionStart: session.SessionStart,
    sessionEnd: session.SessionEnd,
    value: session.SessionId.toString(),
    checkpointOnly: session.CheckpointOnly,
    stageId: session.StageId,
  }));
}
